import { PDFDocument, degrees, StandardFonts, rgb } from 'pdf-lib';
import jsPDF from 'jspdf';

// HYBRID APPROACH:
// - Simple PDF operations run 100% client-side in the browser
// - Complex conversions (PDF↔Word, PDF→Excel) use ConvertX API

// ConvertX API URL - Set this in your .env file as REACT_APP_CONVERTX_API_URL
const CONVERTX_API_URL = process.env.REACT_APP_CONVERTX_API_URL || null;

// Merge multiple PDFs into one
export async function mergePDFs(files) {
  try {
    const mergedPdf = await PDFDocument.create();

    for (const file of files) {
      const arrayBuffer = await file.arrayBuffer();
      const pdf = await PDFDocument.load(arrayBuffer, { ignoreEncryption: true });
      const copiedPages = await mergedPdf.copyPages(pdf, pdf.getPageIndices());
      copiedPages.forEach((page) => mergedPdf.addPage(page));
    }

    const mergedPdfBytes = await mergedPdf.save();
    return new Blob([mergedPdfBytes], { type: 'application/pdf' });
  } catch (error) {
    throw new Error(`Failed to merge PDFs: ${error.message}`);
  }
}

// Split PDF into individual pages
export async function splitPDF(file) {
  try {
    const arrayBuffer = await file.arrayBuffer();
    const pdfDoc = await PDFDocument.load(arrayBuffer, { ignoreEncryption: true });
    const pageCount = pdfDoc.getPageCount();
    const splitPdfs = [];

    // Get original filename without extension
    const originalName = file.name.replace(/\.pdf$/i, '');

    for (let i = 0; i < pageCount; i++) {
      const newPdf = await PDFDocument.create();
      const [copiedPage] = await newPdf.copyPages(pdfDoc, [i]);
      newPdf.addPage(copiedPage);

      // Add metadata
      newPdf.setTitle(`${originalName} - Page ${i + 1}`);
      newPdf.setCreator('PDF Tools Pro');

      const pdfBytes = await newPdf.save();
      splitPdfs.push({
        blob: new Blob([pdfBytes], { type: 'application/pdf' }),
        name: `${originalName}_page_${i + 1}.pdf`
      });
    }

    return splitPdfs;
  } catch (error) {
    throw new Error(`Failed to split PDF: ${error.message}`);
  }
}

// Compress PDF by removing unnecessary data and optimizing
export async function compressPDF(file) {
  try {
    const arrayBuffer = await file.arrayBuffer();
    const pdfDoc = await PDFDocument.load(arrayBuffer, { ignoreEncryption: true });

    // Remove unused objects and compress
    const compressedPdfBytes = await pdfDoc.save({
      useObjectStreams: true,
      addDefaultPage: false,
      objectsPerTick: 50,
    });

    const originalSize = file.size;
    const compressedSize = compressedPdfBytes.length;
    const compressionRatio = ((1 - compressedSize / originalSize) * 100).toFixed(1);

    console.log(`Compression: ${originalSize} → ${compressedSize} bytes (${compressionRatio}% reduction)`);

    return new Blob([compressedPdfBytes], { type: 'application/pdf' });
  } catch (error) {
    throw new Error(`Failed to compress PDF: ${error.message}`);
  }
}

// Convert PDF to JPG images
export async function pdfToJPG(file) {
  try {
    const arrayBuffer = await file.arrayBuffer();

    // Use pdfjs to render PDF pages
    const pdfjsLib = window['pdfjs-dist/build/pdf'];
    if (!pdfjsLib) {
      throw new Error('PDF.js library not loaded');
    }

    pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js`;

    const loadingTask = pdfjsLib.getDocument({ data: arrayBuffer });
    const pdf = await loadingTask.promise;
    const images = [];
    const originalName = file.name.replace(/\.pdf$/i, '');

    for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
      const page = await pdf.getPage(pageNum);
      const scale = 2.5; // Higher quality
      const viewport = page.getViewport({ scale });

      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');
      canvas.height = viewport.height;
      canvas.width = viewport.width;

      // White background
      context.fillStyle = 'white';
      context.fillRect(0, 0, canvas.width, canvas.height);

      await page.render({
        canvasContext: context,
        viewport: viewport
      }).promise;

      const blob = await new Promise(resolve => {
        canvas.toBlob(resolve, 'image/jpeg', 0.92);
      });

      images.push({
        blob,
        name: `${originalName}_page_${pageNum}.jpg`
      });
    }

    return images;
  } catch (error) {
    throw new Error(`Failed to convert PDF to JPG: ${error.message}`);
  }
}

// Convert images (JPG/PNG) to PDF
export async function jpgToPDF(files) {
  try {
    const pdfDoc = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4',
      compress: true
    });

    let firstPage = true;

    for (const file of files) {
      const imageData = await readFileAsDataURL(file);
      const img = await loadImage(imageData);

      // Determine image type
      const imgType = file.type.includes('png') ? 'PNG' : 'JPEG';

      // Calculate dimensions to fit page while maintaining aspect ratio
      const pageWidth = pdfDoc.internal.pageSize.getWidth();
      const pageHeight = pdfDoc.internal.pageSize.getHeight();
      const margin = 10;
      const maxWidth = pageWidth - (margin * 2);
      const maxHeight = pageHeight - (margin * 2);

      let imgWidth = maxWidth;
      let imgHeight = (img.height * maxWidth) / img.width;

      // If image is too tall, scale by height instead
      if (imgHeight > maxHeight) {
        imgHeight = maxHeight;
        imgWidth = (img.width * maxHeight) / img.height;
      }

      // Center the image
      const xPos = (pageWidth - imgWidth) / 2;
      const yPos = (pageHeight - imgHeight) / 2;

      if (!firstPage) {
        pdfDoc.addPage();
      }
      firstPage = false;

      pdfDoc.addImage(imageData, imgType, xPos, yPos, imgWidth, imgHeight);
    }

    const pdfBlob = pdfDoc.output('blob');
    return pdfBlob;
  } catch (error) {
    throw new Error(`Failed to convert images to PDF: ${error.message}`);
  }
}

// Convert PDF to Word - Using ISOLATED ConvertX API endpoint for high-quality conversion (Premium Only)
// This uses a separate API endpoint that won't be affected by other tools
export async function pdfToWord(file) {
  try {
    console.log('Starting PDF to Word conversion (Isolated API - Premium Feature)...');

    // Import supabase client for auth check
    const { supabase, SUPABASE_URL } = await import('./lib/supabase.js');

    // Get current session
    const { data: { session }, error: sessionError } = await supabase.auth.getSession();

    if (sessionError || !session) {
      throw new Error('Please log in to use PDF to Word conversion');
    }

    // Use ISOLATED ConvertX API endpoint if available
    if (CONVERTX_API_URL) {
      console.log('Using ISOLATED PDF-to-Word API endpoint...');

      const formData = new FormData();
      formData.append('file', file);

      // NEW: Uses isolated endpoint /pdf-to-word/convert instead of /convert/sync/pdf/to/docx
      const response = await fetch(`${CONVERTX_API_URL}/pdf-to-word/convert`, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Conversion failed: ${errorText}`);
      }

      const docxBlob = await response.blob();
      console.log('PDF to Word conversion completed successfully!');
      return docxBlob;

    } else {
      // Fallback to Supabase edge function
      console.log('Using Supabase edge function for conversion...');

      const formData = new FormData();
      formData.append('file', file);

      const functionUrl = `${SUPABASE_URL}/functions/v1/pdf-to-word`;

      const response = await fetch(functionUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Conversion failed' }));

        if (response.status === 403) {
          throw new Error('Premium subscription required. PDF to Word conversion is a premium feature that preserves formatting, images, and tables!');
        }

        throw new Error(errorData.error || 'Conversion failed');
      }

      const docxBlob = await response.blob();
      console.log('PDF to Word conversion completed successfully!');
      return docxBlob;
    }

  } catch (error) {
    console.error('PDF to Word conversion failed:', error);
    throw error;
  }
}

// Convert Word to PDF using ISOLATED ConvertX API endpoint
// This uses a separate API endpoint that won't be affected by other tools
export async function wordToPDF(file) {
  try {
    console.log('Starting Word to PDF conversion (Isolated API)...');
    console.log('File details:', { name: file.name, type: file.type, size: file.size });

    // Import supabase client for auth check
    const { supabase, SUPABASE_URL } = await import('./lib/supabase.js');

    // Get current session
    const { data: { session }, error: sessionError } = await supabase.auth.getSession();

    if (sessionError) {
      console.error('Session error:', sessionError);
      throw new Error('Authentication error. Please try logging in again.');
    }

    if (!session) {
      throw new Error('Please log in to use Word to PDF conversion');
    }

    // Use ISOLATED ConvertX API endpoint if available
    if (CONVERTX_API_URL) {
      console.log('Using ISOLATED Word-to-PDF API endpoint...');

      const formData = new FormData();
      formData.append('file', file);

      // NEW: Uses isolated endpoint /word-to-pdf/convert instead of /convert/sync/docx/to/pdf
      const response = await fetch(`${CONVERTX_API_URL}/word-to-pdf/convert`, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Conversion failed: ${errorText}`);
      }

      const pdfBlob = await response.blob();
      console.log('Word to PDF conversion completed successfully!');
      return pdfBlob;

    } else {
      // Fallback to Supabase edge function
      console.log('Using Supabase edge function for conversion...');

      const formData = new FormData();
      formData.append('file', file);

      const functionUrl = `${SUPABASE_URL}/functions/v1/word-to-pdf`;

      const response = await fetch(functionUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: formData,
      });

      if (!response.ok) {
        const errorText = await response.text();
        let errorData;
        try {
          errorData = JSON.parse(errorText);
        } catch (e) {
          errorData = { error: errorText || 'Conversion failed' };
        }
        throw new Error(errorData.error || `Conversion failed with status ${response.status}`);
      }

      const pdfBlob = await response.blob();
      console.log('Word to PDF conversion completed successfully!');
      return pdfBlob;
    }

  } catch (error) {
    console.error('Word to PDF conversion failed:', error);
    throw new Error(`Word to PDF conversion failed: ${error.message}`);
  }
}

// Convert PDF to Excel using ISOLATED ConvertX API endpoint (extracts tables)
// This uses a separate API endpoint that won't be affected by other tools
export async function pdfToExcel(file) {
  try {
    console.log('Starting PDF to Excel conversion (Isolated API)...');

    // Import supabase client for auth check
    const { supabase } = await import('./lib/supabase.js');

    // Get current session
    const { data: { session }, error: sessionError } = await supabase.auth.getSession();

    if (sessionError || !session) {
      throw new Error('Please log in to use PDF to Excel conversion');
    }

    if (!CONVERTX_API_URL) {
      throw new Error('PDF to Excel conversion requires ConvertX API. Please contact support.');
    }

    console.log('Using ISOLATED PDF-to-Excel API endpoint...');

    const formData = new FormData();
    formData.append('file', file);

    // NEW: Uses isolated endpoint /pdf-to-excel/convert instead of /convert/sync/pdf/to/xlsx
    const response = await fetch(`${CONVERTX_API_URL}/pdf-to-excel/convert`, {
      method: 'POST',
      body: formData,
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Conversion failed: ${errorText}`);
    }

    const xlsxBlob = await response.blob();
    console.log('PDF to Excel conversion completed successfully!');
    return xlsxBlob;

  } catch (error) {
    console.error('PDF to Excel conversion failed:', error);
    throw error;
  }
}

// Convert Excel to PDF using ISOLATED ConvertX API endpoint (preserves formatting)
// This uses a separate API endpoint that won't be affected by other tools
export async function excelToPDF(file) {
  try {
    console.log('Starting Excel to PDF conversion (Isolated API)...');
    console.log('File details:', { name: file.name, type: file.type, size: file.size });

    // Import supabase client for auth check
    const { supabase, SUPABASE_URL } = await import('./lib/supabase.js');

    // Get current session
    const { data: { session }, error: sessionError } = await supabase.auth.getSession();

    if (sessionError) {
      console.error('Session error:', sessionError);
      throw new Error('Authentication error. Please try logging in again.');
    }

    if (!session) {
      throw new Error('Please log in to use Excel to PDF conversion');
    }

    // Use ISOLATED ConvertX API endpoint if available
    if (CONVERTX_API_URL) {
      console.log('Using ISOLATED Excel-to-PDF API endpoint...');

      const formData = new FormData();
      formData.append('file', file);

      // NEW: Uses isolated endpoint /excel-to-pdf/convert instead of /convert/sync/xlsx/to/pdf
      const response = await fetch(`${CONVERTX_API_URL}/excel-to-pdf/convert`, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Conversion failed: ${errorText}`);
      }

      const pdfBlob = await response.blob();
      console.log('Excel to PDF conversion completed successfully!');
      return pdfBlob;

    } else {
      // Fallback to Supabase edge function
      console.log('Using Supabase edge function for conversion...');

      const formData = new FormData();
      formData.append('file', file);

      const functionUrl = `${SUPABASE_URL}/functions/v1/excel-to-pdf`;

      const response = await fetch(functionUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: formData,
      });

      if (!response.ok) {
        const errorText = await response.text();
        let errorData;
        try {
          errorData = JSON.parse(errorText);
        } catch (e) {
          errorData = { error: errorText || 'Conversion failed' };
        }
        throw new Error(errorData.error || `Conversion failed with status ${response.status}`);
      }

      const pdfBlob = await response.blob();
      console.log('Excel to PDF conversion completed successfully!');
      return pdfBlob;
    }

  } catch (error) {
    console.error('Excel to PDF conversion failed:', error);
    throw new Error(`Excel to PDF conversion failed: ${error.message}`);
  }
}

// Helper function to create PDF from text with better formatting
// eslint-disable-next-line no-unused-vars
function createPDFFromText(text, originalFilename = 'document') {
  const pdfDoc = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4',
    compress: true
  });

  // Add metadata
  pdfDoc.setProperties({
    title: originalFilename.replace(/\.(docx?|txt)$/i, ''),
    author: 'PDF Tools Pro',
    creator: 'PDF Tools Pro'
  });

  // Set font
  pdfDoc.setFont('helvetica');
  pdfDoc.setFontSize(11);

  const pageWidth = pdfDoc.internal.pageSize.getWidth();
  const pageHeight = pdfDoc.internal.pageSize.getHeight();
  const margin = 20;
  const maxWidth = pageWidth - (margin * 2);

  let y = margin;
  const lineHeight = 7;
  const paragraphSpacing = 3;

  // Split text into paragraphs
  const paragraphs = text.split(/\n\n+/);

  paragraphs.forEach((paragraph) => {
    // Skip empty paragraphs
    if (!paragraph.trim()) return;

    // Detect heading styles
    const isHeading = paragraph.length < 60 &&
                     (paragraph === paragraph.toUpperCase() ||
                      paragraph.startsWith('#') ||
                      /^(\d+\.|\*|-)\s/.test(paragraph));

    if (isHeading) {
      pdfDoc.setFontSize(14);
      pdfDoc.setFont('helvetica', 'bold');
    } else {
      pdfDoc.setFontSize(11);
      pdfDoc.setFont('helvetica', 'normal');
    }

    // Clean the paragraph
    const cleanParagraph = paragraph.replace(/^#+\s*/, '').trim();

    // Split paragraph into lines that fit the page width
    const lines = pdfDoc.splitTextToSize(cleanParagraph, maxWidth);

    lines.forEach((line) => {
      // Check if we need a new page
      if (y + lineHeight > pageHeight - margin) {
        pdfDoc.addPage();
        y = margin;
      }

      pdfDoc.text(line, margin, y);
      y += lineHeight;
    });

    // Add extra space after paragraph
    y += paragraphSpacing;
  });

  const pdfBlob = pdfDoc.output('blob');
  return pdfBlob;
}

// Rotate PDF pages
export async function rotatePDF(file, rotation = 90) {
  try {
    const arrayBuffer = await file.arrayBuffer();
    const pdfDoc = await PDFDocument.load(arrayBuffer, { ignoreEncryption: true });
    const pages = pdfDoc.getPages();

    pages.forEach(page => {
      const currentRotation = page.getRotation().angle;
      page.setRotation(degrees(currentRotation + rotation));
    });

    const rotatedPdfBytes = await pdfDoc.save();
    return new Blob([rotatedPdfBytes], { type: 'application/pdf' });
  } catch (error) {
    throw new Error(`Failed to rotate PDF: ${error.message}`);
  }
}

// Add custom watermark to PDF
export async function protectPDF(file, watermarkText, options = {}) {
  try {
    // Extract options with defaults
    const {
      watermarkType = 'text',
      opacity = 0.15,
      fontSize = 80,
      rotation = 45,
      color = '#999999',
      logoFile,
      logoSize = 100
    } = options;

    const arrayBuffer = await file.arrayBuffer();
    const pdfDoc = await PDFDocument.load(arrayBuffer, { ignoreEncryption: true });
    const pages = pdfDoc.getPages();

    // Handle text watermark
    if (watermarkType === 'text') {
      const font = await pdfDoc.embedFont(StandardFonts.HelveticaBold);

      // Helper to convert hex color to RGB
      const hexToRgb = (hex) => {
        const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
        return result ? {
          r: parseInt(result[1], 16) / 255,
          g: parseInt(result[2], 16) / 255,
          b: parseInt(result[3], 16) / 255
        } : { r: 0.6, g: 0.6, b: 0.6 };
      };

      const colorRgb = hexToRgb(color);

      pages.forEach(page => {
        const { width, height } = page.getSize();
        const textWidth = font.widthOfTextAtSize(watermarkText, fontSize);
        const radians = (rotation * Math.PI) / 180;
        const centerX = width / 2 - (textWidth * Math.cos(radians)) / 2;
        const centerY = height / 2;

        page.drawText(watermarkText, {
          x: centerX,
          y: centerY,
          size: fontSize,
          font: font,
          color: rgb(colorRgb.r, colorRgb.g, colorRgb.b),
          opacity: opacity,
          rotate: degrees(rotation),
        });
      });
    }
    // Handle image watermark
    else if (watermarkType === 'image' && logoFile) {
      // Read logo file as array buffer
      const logoArrayBuffer = await logoFile.arrayBuffer();

      // Determine image type and embed
      let embeddedImage;
      if (logoFile.type === 'image/png') {
        embeddedImage = await pdfDoc.embedPng(logoArrayBuffer);
      } else if (logoFile.type === 'image/jpeg' || logoFile.type === 'image/jpg') {
        embeddedImage = await pdfDoc.embedJpg(logoArrayBuffer);
      } else {
        // Try PNG as default
        embeddedImage = await pdfDoc.embedPng(logoArrayBuffer);
      }

      pages.forEach(page => {
        const { width, height } = page.getSize();

        // Calculate position (centered)
        const x = width / 2 - logoSize / 2;
        const y = height / 2 - logoSize / 2;

        page.drawImage(embeddedImage, {
          x,
          y,
          width: logoSize,
          height: logoSize,
          opacity: opacity,
          rotate: degrees(rotation),
        });
      });
    }

    // Add metadata
    pdfDoc.setTitle(`Watermarked: ${watermarkText}`);
    pdfDoc.setAuthor('PDF Tools Pro');
    pdfDoc.setSubject(`Watermarked with: ${watermarkText}`);

    const pdfBytes = await pdfDoc.save();
    return new Blob([pdfBytes], { type: 'application/pdf' });
  } catch (error) {
    throw new Error(`Failed to add watermark: ${error.message}`);
  }
}

// DEPRECATED - Encrypt/Decrypt PDF removed (required backend server)
// These features have been disabled to achieve 100% serverless architecture
//
// NOTE: Browser-based PDF libraries (pdf-lib) do not support AES password encryption
// To re-enable these features, you would need to deploy a backend server
//
// export async function encryptPDF(file, password) {
//   throw new Error('PDF encryption requires a backend server and has been disabled in the serverless version');
// }
//
// export async function unlockPDF(file, password) {
//   throw new Error('PDF decryption requires a backend server and has been disabled in the serverless version');
// }

// Helper function to read file as Data URL
function readFileAsDataURL(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

// Helper function to load image
function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

// PDF to PNG (same as JPG but PNG format)
export async function pdfToPNG(file) {
  try {
    const arrayBuffer = await file.arrayBuffer();
    const pdfjsLib = window['pdfjs-dist/build/pdf'];
    if (!pdfjsLib) {
      throw new Error('PDF.js library not loaded');
    }

    pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js`;

    const loadingTask = pdfjsLib.getDocument({ data: arrayBuffer });
    const pdf = await loadingTask.promise;
    const images = [];
    const originalName = file.name.replace(/\.pdf$/i, '');

    for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
      const page = await pdf.getPage(pageNum);
      const scale = 2.5;
      const viewport = page.getViewport({ scale });

      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');
      canvas.height = viewport.height;
      canvas.width = viewport.width;

      context.fillStyle = 'white';
      context.fillRect(0, 0, canvas.width, canvas.height);

      await page.render({
        canvasContext: context,
        viewport: viewport
      }).promise;

      const blob = await new Promise(resolve => {
        canvas.toBlob(resolve, 'image/png');
      });

      images.push({
        blob,
        name: `${originalName}_page_${pageNum}.png`
      });
    }

    return images;
  } catch (error) {
    throw new Error(`Failed to convert PDF to PNG: ${error.message}`);
  }
}

// Add page numbers to PDF
export async function addPageNumbers(file, position = 'bottom-center', startNumber = 1) {
  try {
    const arrayBuffer = await file.arrayBuffer();
    const pdfDoc = await PDFDocument.load(arrayBuffer, { ignoreEncryption: true });
    const pages = pdfDoc.getPages();
    const font = await pdfDoc.embedFont(StandardFonts.Helvetica);

    pages.forEach((page, index) => {
      const { width, height } = page.getSize();
      const pageNumber = startNumber + index;
      const text = `${pageNumber}`;
      const fontSize = 12;
      const textWidth = font.widthOfTextAtSize(text, fontSize);

      let x, y;
      const margin = 30;

      // Determine position
      if (position.includes('bottom')) {
        y = margin;
      } else if (position.includes('top')) {
        y = height - margin;
      } else {
        y = height / 2;
      }

      if (position.includes('center')) {
        x = (width - textWidth) / 2;
      } else if (position.includes('right')) {
        x = width - textWidth - margin;
      } else {
        x = margin;
      }

      page.drawText(text, {
        x,
        y,
        size: fontSize,
        font: font,
        color: rgb(0, 0, 0),
      });
    });

    const pdfBytes = await pdfDoc.save();
    return new Blob([pdfBytes], { type: 'application/pdf' });
  } catch (error) {
    throw new Error(`Failed to add page numbers: ${error.message}`);
  }
}

// Extract specific pages from PDF
export async function extractPages(file, pageNumbers) {
  try {
    const arrayBuffer = await file.arrayBuffer();
    const pdfDoc = await PDFDocument.load(arrayBuffer, { ignoreEncryption: true });
    const newPdf = await PDFDocument.create();

    // Convert page numbers to zero-indexed
    const pageIndices = pageNumbers.map(num => num - 1);

    const copiedPages = await newPdf.copyPages(pdfDoc, pageIndices);
    copiedPages.forEach((page) => newPdf.addPage(page));

    const pdfBytes = await newPdf.save();
    return new Blob([pdfBytes], { type: 'application/pdf' });
  } catch (error) {
    throw new Error(`Failed to extract pages: ${error.message}`);
  }
}

// Delete specific pages from PDF
export async function deletePages(file, pageNumbers) {
  try {
    const arrayBuffer = await file.arrayBuffer();
    const pdfDoc = await PDFDocument.load(arrayBuffer, { ignoreEncryption: true });
    const totalPages = pdfDoc.getPageCount();

    // Create array of pages to keep
    const pagesToKeep = [];
    for (let i = 1; i <= totalPages; i++) {
      if (!pageNumbers.includes(i)) {
        pagesToKeep.push(i - 1); // Convert to zero-indexed
      }
    }

    const newPdf = await PDFDocument.create();
    const copiedPages = await newPdf.copyPages(pdfDoc, pagesToKeep);
    copiedPages.forEach((page) => newPdf.addPage(page));

    const pdfBytes = await newPdf.save();
    return new Blob([pdfBytes], { type: 'application/pdf' });
  } catch (error) {
    throw new Error(`Failed to delete pages: ${error.message}`);
  }
}

// Extract text from PDF (standalone tool)
export async function extractText(file) {
  try {
    const arrayBuffer = await file.arrayBuffer();
    const pdfjsLib = window['pdfjs-dist/build/pdf'];

    if (pdfjsLib) {
      pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js`;

      const loadingTask = pdfjsLib.getDocument({ data: arrayBuffer });
      const pdf = await loadingTask.promise;
      let fullText = '';

      for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
        const page = await pdf.getPage(pageNum);
        const textContent = await page.getTextContent();
        const pageText = textContent.items.map(item => item.str).join(' ');
        fullText += `\n\n--- Page ${pageNum} ---\n\n${pageText}`;
      }

      const textBlob = new Blob([fullText], { type: 'text/plain' });
      return textBlob;
    }

    throw new Error('PDF.js library not loaded');
  } catch (error) {
    throw new Error(`Failed to extract text: ${error.message}`);
  }
}

// Add signature to PDF (image-based)
export async function addSignature(file, signatureImageFile, position = 'bottom-right') {
  try {
    const arrayBuffer = await file.arrayBuffer();
    const pdfDoc = await PDFDocument.load(arrayBuffer, { ignoreEncryption: true });

    // Load signature image
    const signatureBytes = await signatureImageFile.arrayBuffer();
    let signatureImage;

    if (signatureImageFile.type === 'image/png') {
      signatureImage = await pdfDoc.embedPng(signatureBytes);
    } else {
      signatureImage = await pdfDoc.embedJpg(signatureBytes);
    }

    const pages = pdfDoc.getPages();
    const lastPage = pages[pages.length - 1];
    const { width, height } = lastPage.getSize();

    const signatureWidth = 150;
    const signatureHeight = 50;
    const margin = 50;

    let x, y;
    if (position === 'bottom-right') {
      x = width - signatureWidth - margin;
      y = margin;
    } else if (position === 'bottom-left') {
      x = margin;
      y = margin;
    } else if (position === 'top-right') {
      x = width - signatureWidth - margin;
      y = height - signatureHeight - margin;
    } else {
      x = margin;
      y = height - signatureHeight - margin;
    }

    lastPage.drawImage(signatureImage, {
      x,
      y,
      width: signatureWidth,
      height: signatureHeight,
    });

    const pdfBytes = await pdfDoc.save();
    return new Blob([pdfBytes], { type: 'application/pdf' });
  } catch (error) {
    throw new Error(`Failed to add signature: ${error.message}`);
  }
}

// Edit PDF Metadata
export async function editMetadata(file, metadata) {
  try {
    const arrayBuffer = await file.arrayBuffer();
    const pdfDoc = await PDFDocument.load(arrayBuffer, { ignoreEncryption: true });

    if (metadata.title) pdfDoc.setTitle(metadata.title);
    if (metadata.author) pdfDoc.setAuthor(metadata.author);
    if (metadata.subject) pdfDoc.setSubject(metadata.subject);
    if (metadata.keywords) pdfDoc.setKeywords(metadata.keywords.split(',').map(k => k.trim()));
    if (metadata.creator) pdfDoc.setCreator(metadata.creator);
    if (metadata.producer) pdfDoc.setProducer(metadata.producer);

    const pdfBytes = await pdfDoc.save();
    return new Blob([pdfBytes], { type: 'application/pdf' });
  } catch (error) {
    throw new Error(`Failed to edit metadata: ${error.message}`);
  }
}

// Extract images from PDF
export async function extractImages(file) {
  try {
    // Note: pdf-lib doesn't have direct image extraction
    // We'll convert pages to images as a workaround
    return await pdfToJPG(file);
  } catch (error) {
    throw new Error(`Failed to extract images: ${error.message}`);
  }
}

// Crop PDF pages
export async function cropPDF(file, margins = { top: 50, bottom: 50, left: 50, right: 50 }) {
  try {
    const arrayBuffer = await file.arrayBuffer();
    const pdfDoc = await PDFDocument.load(arrayBuffer, { ignoreEncryption: true });
    const pages = pdfDoc.getPages();

    pages.forEach(page => {
      const { width, height } = page.getSize();

      page.setCropBox(
        margins.left,
        margins.bottom,
        width - margins.left - margins.right,
        height - margins.top - margins.bottom
      );
    });

    const pdfBytes = await pdfDoc.save();
    return new Blob([pdfBytes], { type: 'application/pdf' });
  } catch (error) {
    throw new Error(`Failed to crop PDF: ${error.message}`);
  }
}

// Resize PDF pages
export async function resizePDF(file, pageSize = 'A4') {
  try {
    const arrayBuffer = await file.arrayBuffer();
    const pdfDoc = await PDFDocument.load(arrayBuffer, { ignoreEncryption: true });
    const newPdf = await PDFDocument.create();

    // Page size dimensions in points (1 inch = 72 points)
    const pageSizes = {
      'A4': { width: 595, height: 842 },
      'Letter': { width: 612, height: 792 },
      'Legal': { width: 612, height: 1008 },
      'A3': { width: 842, height: 1191 },
      'A5': { width: 420, height: 595 },
    };

    const targetSize = pageSizes[pageSize] || pageSizes['A4'];
    const pages = pdfDoc.getPages();

    for (let i = 0; i < pages.length; i++) {
      const [copiedPage] = await newPdf.copyPages(pdfDoc, [i]);
      copiedPage.setSize(targetSize.width, targetSize.height);
      newPdf.addPage(copiedPage);
    }

    const pdfBytes = await newPdf.save();
    return new Blob([pdfBytes], { type: 'application/pdf' });
  } catch (error) {
    throw new Error(`Failed to resize PDF: ${error.message}`);
  }
}

// Flatten PDF (make form fields non-editable)
export async function flattenPDF(file) {
  try {
    const arrayBuffer = await file.arrayBuffer();
    const pdfDoc = await PDFDocument.load(arrayBuffer, { ignoreEncryption: true });

    const form = pdfDoc.getForm();
    const fields = form.getFields();

    // Flatten all form fields
    fields.forEach(field => {
      try {
        form.flatten();
      } catch (e) {
        console.warn('Could not flatten field:', field.getName());
      }
    });

    const pdfBytes = await pdfDoc.save();
    return new Blob([pdfBytes], { type: 'application/pdf' });
  } catch (error) {
    throw new Error(`Failed to flatten PDF: ${error.message}`);
  }
}

// Add header and footer to PDF
export async function addHeaderFooter(file, headerText = '', footerText = '') {
  try {
    const arrayBuffer = await file.arrayBuffer();
    const pdfDoc = await PDFDocument.load(arrayBuffer, { ignoreEncryption: true });
    const pages = pdfDoc.getPages();
    const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
    const fontSize = 10;

    pages.forEach((page, index) => {
      const { width, height } = page.getSize();
      const margin = 30;

      // Add header
      if (headerText) {
        const processedHeader = headerText.replace('{page}', index + 1).replace('{total}', pages.length);
        const headerWidth = font.widthOfTextAtSize(processedHeader, fontSize);
        page.drawText(processedHeader, {
          x: (width - headerWidth) / 2,
          y: height - margin,
          size: fontSize,
          font: font,
          color: rgb(0, 0, 0),
        });
      }

      // Add footer
      if (footerText) {
        const processedFooter = footerText.replace('{page}', index + 1).replace('{total}', pages.length);
        const footerWidth = font.widthOfTextAtSize(processedFooter, fontSize);
        page.drawText(processedFooter, {
          x: (width - footerWidth) / 2,
          y: margin,
          size: fontSize,
          font: font,
          color: rgb(0, 0, 0),
        });
      }
    });

    const pdfBytes = await pdfDoc.save();
    return new Blob([pdfBytes], { type: 'application/pdf' });
  } catch (error) {
    throw new Error(`Failed to add header/footer: ${error.message}`);
  }
}

// Organize Pages - Reorder pages based on custom order
export async function organizePages(file, pageOrder) {
  try {
    const arrayBuffer = await file.arrayBuffer();
    const pdfDoc = await PDFDocument.load(arrayBuffer, { ignoreEncryption: true });

    const newDoc = await PDFDocument.create();

    // Copy pages in the specified order
    for (const pageNum of pageOrder) {
      const [copiedPage] = await newDoc.copyPages(pdfDoc, [pageNum - 1]); // pageNum is 1-indexed
      newDoc.addPage(copiedPage);
    }

    const pdfBytes = await newDoc.save();
    return new Blob([pdfBytes], { type: 'application/pdf' });
  } catch (error) {
    throw new Error(`Failed to organize pages: ${error.message}`);
  }
}

// Get file size in MB
export function getFileSizeMB(blob) {
  return (blob.size / (1024 * 1024)).toFixed(2);
}
